import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { supabase } from "../../supabaseClient";

const MAX_ATTACHMENTS = 3;
const MAX_TAGS = 8;

const createEmptyForm = () => ({
  idea: "",
  tags: [],
});

function createFormState(ideaToEdit) {
  if (!ideaToEdit) return createEmptyForm();

  return {
    idea: ideaToEdit.description || "",
    tags: Array.isArray(ideaToEdit.tags) ? ideaToEdit.tags.filter(Boolean) : [],
  };
}

function getAttachmentNameFromUrl(url, index) {
  try {
    const { pathname } = new URL(url);
    const rawName = decodeURIComponent(pathname.split("/").pop() || `attachment_${index + 1}`);
    return rawName.replace(/^\d+_/, "");
  } catch {
    return `Attachment ${index + 1}`;
  }
}

function createExistingAttachments(ideaToEdit) {
  if (!ideaToEdit || !Array.isArray(ideaToEdit.attachment_urls)) return [];

  return ideaToEdit.attachment_urls.filter(Boolean).map((url, index) => ({
    id: `existing_${index}_${Math.random().toString(36).slice(2, 8)}`,
    url,
    previewUrl: url,
    name: getAttachmentNameFromUrl(url, index),
    sizeLabel: "Saved file",
    isExisting: true,
    shouldRevokePreview: false,
  }));
}

function normalizeTag(value) {
  return value.replace(/^#+/, "").replace(/\s+/g, " ").trim();
}

function createIdeaTitle(ideaText) {
  if (!ideaText) return "Untitled Idea";
  const withBreaks = ideaText
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .trim();
  const lines = withBreaks.split(/\n/).filter(Boolean);
  const firstLine = (lines[0] || "").replace(/\s+/g, " ").trim();
  if (!firstLine) return "Untitled Idea";
  const hasMoreContent = lines.length > 1;
  if (firstLine.length > 72) return `${firstLine.slice(0, 69).trim()}...`;
  return hasMoreContent ? `${firstLine}...` : firstLine;
}

function stripHtml(value) {
  if (!value) return "";

  if (typeof window !== "undefined") {
    const parsed = new window.DOMParser().parseFromString(value, "text/html");
    return parsed.body.textContent || "";
  }

  return value.replace(/<[^>]*>/g, " ");
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function sanitizeFileName(fileName) {
  return fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
}

export default function QuickIdeaPopup({
  show,
  onClose,
  onSaved,
  userId,
  sessionId,
  ideaToEdit = null,
  onDelete,
}) {
  const ideaInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentsRef = useRef([]);
  const [activeIdea, setActiveIdea] = useState(ideaToEdit);
  const [closeOnEditSave, setCloseOnEditSave] = useState(Boolean(ideaToEdit?.id));
  const [sessionIdeas, setSessionIdeas] = useState([]);
  const [recentlySavedId, setRecentlySavedId] = useState(null);
  const [form, setForm] = useState(() => createFormState(ideaToEdit));
  const [attachments, setAttachments] = useState(() => createExistingAttachments(ideaToEdit));
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [existingTags, setExistingTags] = useState([]);

  const quillModules = useMemo(() => ({
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["link", "code-block"],
      ["clean"],
    ],
  }), []);

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "align",
    "size",
    "link",
    "code-block",
  ];

  const focusIdeaEditor = useCallback(() => {
    requestAnimationFrame(() => {
      const quillEditor = ideaInputRef.current?.getEditor?.();
      quillEditor?.focus?.();
    });
  }, []);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((attachment) => {
        if (attachment.previewUrl && attachment.shouldRevokePreview) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!show || !userId) return;

    const fetchTags = async () => {
      const { data } = await supabase
        .from("quick_ideas")
        .select("tags")
        .eq("user_id", userId);

      if (data) {
        const allTags = [...new Set(data.flatMap(d => d.tags || []).filter(Boolean))];
        setExistingTags(allTags);
      }
    };
    fetchTags();
  }, [show, userId]);

  const clearAttachmentObjects = useCallback((items) => {
    items.forEach((attachment) => {
      if (attachment.previewUrl && attachment.shouldRevokePreview) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    });
  }, []);

  const resetForm = useCallback(() => {
    clearAttachmentObjects(attachmentsRef.current);
    attachmentsRef.current = [];
    setAttachments([]);
    setForm(createEmptyForm());
    setTagInput("");
    setError("");
    setActiveIdea(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [clearAttachmentObjects]);

  const applyIdeaToEditor = useCallback((nextIdea) => {
    clearAttachmentObjects(attachmentsRef.current);
    const nextAttachments = createExistingAttachments(nextIdea);
    attachmentsRef.current = nextAttachments;
    setAttachments(nextAttachments);
    setForm(createFormState(nextIdea));
    setTagInput("");
    setError("");
    setActiveIdea(nextIdea || null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    focusIdeaEditor();
  }, [clearAttachmentObjects, focusIdeaEditor]);

  const handleClose = () => {
    if (saving) return;
    resetForm();
    setSessionIdeas([]);
    setRecentlySavedId(null);
    onClose?.();
  };

  useEffect(() => {
    if (!show) return;

    setCloseOnEditSave(Boolean(ideaToEdit?.id));
    setSessionIdeas(ideaToEdit?.id ? [ideaToEdit] : []);
    setRecentlySavedId(null);
    applyIdeaToEditor(ideaToEdit || null);
  }, [show, ideaToEdit, applyIdeaToEditor]);

  const handleDelete = async () => {
    const targetIdea = activeIdea || ideaToEdit;
    if (saving || !targetIdea?.id) return;

    setSaving(true);
    setError("");

    try {
      let deleted = false;

      if (onDelete) {
        deleted = await onDelete(targetIdea);
      } else {
        const { error: deleteError } = await supabase
          .from("quick_ideas")
          .delete()
          .eq("id", targetIdea.id)
          .eq("user_id", userId);

        if (deleteError) throw deleteError;

        deleted = true;

        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("quick-ideas:changed", {
              detail: {
                action: "deleted",
                idea: { id: targetIdea.id },
              },
            }),
          );
        }
      }

      if (deleted !== false) {
        setSessionIdeas((prev) => prev.filter((idea) => idea.id !== targetIdea.id));
        resetForm();
        setRecentlySavedId(null);
      }
    } catch (deleteError) {
      setError(deleteError?.message || "The ideas could not be deleted.");
    } finally {
      setSaving(false);
    }
  };

  const handleSelectSavedIdea = (idea) => {
    setCloseOnEditSave(false);
    applyIdeaToEditor(idea);
  };

  const handleStartNewIdea = () => {
    setCloseOnEditSave(false);
    setRecentlySavedId(null);
    resetForm();
    focusIdeaEditor();
  };

  const deleteIdeaFromRail = async (targetIdea) => {
    if (saving || !targetIdea?.id || !userId) return;

    setSaving(true);
    setError("");

    try {
      const { error: deleteError } = await supabase
        .from("quick_ideas")
        .delete()
        .eq("id", targetIdea.id)
        .eq("user_id", userId);

      if (deleteError) throw deleteError;

      setSessionIdeas((prev) => prev.filter((idea) => idea.id !== targetIdea.id));

      if (activeIdea?.id === targetIdea.id) {
        resetForm();
      }

      if (recentlySavedId === targetIdea.id) {
        setRecentlySavedId(null);
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("quick-ideas:changed", {
            detail: {
              action: "deleted",
              idea: { id: targetIdea.id },
            },
          }),
        );
      }
    } catch (deleteIdeaError) {
      setError(deleteIdeaError?.message || "The ideas could not be deleted.");
    } finally {
      setSaving(false);
    }
  };

  const addTag = (rawValue) => {
    const tag = normalizeTag(rawValue);
    if (!tag) return;

    setError("");
    setForm((prev) => {
      const alreadyExists = prev.tags.some(
        (existingTag) => existingTag.toLowerCase() === tag.toLowerCase(),
      );
      if (alreadyExists || prev.tags.length >= MAX_TAGS) return prev;
      return {
        ...prev,
        tags: [...prev.tags, tag],
      };
    });

    setTagInput("");
  };

  const removeTag = (indexToRemove) => {
    setError("");
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== indexToRemove),
    }));
  };

  const clearTags = () => {
    setError("");
    setTagInput("");
    setForm((prev) => ({ ...prev, tags: [] }));
  };

  const handleTagInputKeyDown = (event) => {
    if ((event.key === "Enter" || event.key === "," || event.key === "Tab") && tagInput.trim()) {
      event.preventDefault();
      addTag(tagInput);
    }
  };

  const handleTagInputBlur = () => {
    if (tagInput.trim()) {
      addTag(tagInput);
    }
  };

  const handleAttachmentSelect = (event) => {
    const incomingFiles = Array.from(event.target.files || []);
    if (!incomingFiles.length) return;

    setError("");

    const remainingSlots = MAX_ATTACHMENTS - attachments.length;
    if (remainingSlots <= 0) {
      setError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const nextFiles = incomingFiles.slice(0, remainingSlots).map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      name: file.name,
      previewUrl: URL.createObjectURL(file),
      sizeLabel: formatFileSize(file.size),
      isExisting: false,
      shouldRevokePreview: true,
    }));

    if (incomingFiles.length > remainingSlots) {
      setError(
        `Only ${remainingSlots} more attachment${remainingSlots === 1 ? "" : "s"} can be added.`,
      );
    }

    setAttachments((prev) => [...prev, ...nextFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (attachmentId) => {
    setError("");
    setAttachments((prev) => {
      const target = prev.find((attachment) => attachment.id === attachmentId);
      if (target?.previewUrl && target.shouldRevokePreview) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((attachment) => attachment.id !== attachmentId);
    });
  };

  const openAttachmentPreview = (previewUrl) => {
    window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  const uploadAttachmentFile = async (file) => {
    const timestamp = Date.now();
    const safeFileName = sanitizeFileName(file.name);
    const baseFolder = `${userId}/quick-ideas`;
    const sessionFolder = sessionId || "standalone";
    const filePath = `${baseFolder}/${sessionFolder}/${timestamp}_${safeFileName}`;

    const { error: uploadError } = await supabase.storage
      .from("Storage")
      .upload(filePath, file, {
        upsert: false,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage.from("Storage").getPublicUrl(filePath);
    return {
      filePath,
      publicUrl: data.publicUrl,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!userId) {
      setError("Your session is still loading. Please try again.");
      return;
    }

    const plainIdea = stripHtml(form.idea).replace(/\s+/g, " ").trim();
    if (!plainIdea) {
      setError("Please write your ideas first.");
      return;
    }

    if (attachments.length > MAX_ATTACHMENTS) {
      setError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      return;
    }

    setSaving(true);
    setError("");

    const uploadedFiles = [];
    const isEditing = Boolean(activeIdea?.id);

    try {
      const generatedTitle = createIdeaTitle(form.idea);

      for (const attachment of attachments) {
        if (attachment.isExisting || !attachment.file) continue;
        const uploadedFile = await uploadAttachmentFile(attachment.file);
        uploadedFiles.push(uploadedFile);
      }

      const attachmentUrls = [
        ...attachments
          .filter((attachment) => attachment.isExisting && attachment.url)
          .map((attachment) => attachment.url),
        ...uploadedFiles.map((file) => file.publicUrl),
      ];

      const payload = {
        title: generatedTitle,
        description: form.idea,
        attachment_urls: attachmentUrls,
        tags: form.tags,
      };

      const operation = isEditing
        ? supabase
            .from("quick_ideas")
            .update(payload)
            .eq("id", activeIdea.id)
            .eq("user_id", userId)
        : supabase.from("quick_ideas").insert([
            {
              user_id: userId,
              session_id: sessionId || null,
              ...payload,
            },
          ]);

      const { data, error: saveQueryError } = await operation
        .select("id, title, description, tags, attachment_urls, created_at")
        .single();

      if (saveQueryError) {
        if (uploadedFiles.length > 0) {
          await supabase.storage
            .from("Storage")
            .remove(uploadedFiles.map((file) => file.filePath));
        }

        if (saveQueryError.message?.includes("does not exist")) {
          setError("The quick ideas table is missing. Run `migrations/040_quick_ideas.sql` first.");
          setSaving(false);
          return;
        }

        throw saveQueryError;
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("quick-ideas:changed", {
            detail: {
              action: isEditing ? "updated" : "saved",
              idea: data,
            },
          }),
        );
      }

      const nextIdeaState = {
        ...(activeIdea || {}),
        ...payload,
        created_at: data.created_at,
        id: data.id,
      };

      setSessionIdeas((prev) => {
        if (isEditing) {
          return prev.map((ideaItem) =>
            ideaItem.id === nextIdeaState.id ? { ...ideaItem, ...nextIdeaState } : ideaItem,
          );
        }

        return [nextIdeaState, ...prev];
      });

      setRecentlySavedId(data.id);
      onSaved?.(data);

      if (isEditing && closeOnEditSave) {
        onClose?.();
      } else if (isEditing) {
        applyIdeaToEditor(nextIdeaState);
      } else {
        resetForm();
        focusIdeaEditor();
      }
    } catch (saveError) {
      if (uploadedFiles.length > 0) {
        await supabase.storage
          .from("Storage")
          .remove(uploadedFiles.map((file) => file.filePath));
      }

      setError(saveError?.message || "The ideas could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={saving ? "static" : true}
      keyboard={!saving}
      className="quick-idea-modal"
      dialogClassName="quick-idea-modal-dialog"
      onEntered={focusIdeaEditor}
    >
      <Modal.Header className="quick-idea-modal__header">
        <div className="quick-idea-modal__header-main">
          <Modal.Title className="quick-idea-modal__title-wrap">
            <div className="quick-idea-popup__eyebrow">
              <i className="bi bi-lightbulb-fill"></i>
              {activeIdea ? "Edit Ideas" : "Quick Ideas"}
            </div>
            <div className="quick-idea-popup__title">
              {activeIdea ? "Update your saved ideas." : "Capture your ideas before they slip away."}
            </div>
          </Modal.Title>

          <div className="quick-idea-modal__header-actions">
            {activeIdea ? (
              <button
                type="button"
                className="quick-idea-modal__delete-btn"
                onClick={handleDelete}
                disabled={saving}
              >
                <i className="bi bi-trash"></i>
                Delete
              </button>
            ) : null}

            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
              disabled={saving}
            ></button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="quick-idea-modal__body">
        <div className={`quick-idea-popup__layout ${sessionIdeas.length > 0 ? "has-sidebar" : ""}`}>
          <aside className={`quick-idea-popup__sidebar ${sessionIdeas.length > 0 ? "is-visible" : ""}`}>
            <div className="quick-idea-popup__sidebar-topbar">
              <button
                type="button"
                className="quick-idea-popup__sidebar-new-btn"
                onClick={handleStartNewIdea}
                disabled={saving}
              >
                <i className="bi bi-plus-lg"></i>
                New
              </button>
            </div>

            <div className="quick-idea-popup__saved-list">
              {sessionIdeas.map((savedIdea) => {
                const savedIdeaTags = Array.isArray(savedIdea.tags)
                  ? savedIdea.tags.filter(Boolean).slice(0, 2)
                  : [];

                return (
                  <div
                    key={savedIdea.id}
                    className={`quick-idea-popup__saved-shell ${activeIdea?.id === savedIdea.id ? "is-active" : ""} ${recentlySavedId === savedIdea.id ? "is-recent" : ""}`}
                  >
                    <button
                      type="button"
                      className="quick-idea-popup__saved-item"
                      onClick={() => handleSelectSavedIdea(savedIdea)}
                    >
                      <div className="quick-idea-popup__saved-item-title">
                        {savedIdea.title || "Untitled ideas"}
                      </div>
                      <div className="quick-idea-popup__saved-item-tags">
                        {savedIdeaTags.length > 0
                          ? savedIdeaTags.map((tag) => (
                              <span key={`${savedIdea.id}-${tag}`}>{tag}</span>
                            ))
                          : <span>No tags</span>}
                      </div>
                    </button>

                    <button
                      type="button"
                      className="quick-idea-popup__saved-delete"
                      onClick={() => deleteIdeaFromRail(savedIdea)}
                      aria-label={`Delete ${savedIdea.title || "idea"}`}
                      title="Delete idea"
                      disabled={saving}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          </aside>

          <form onSubmit={handleSubmit} className="quick-idea-popup__form">
            {error ? <div className="quick-idea-popup__error">{error}</div> : null}

            <div className="quick-idea-popup__field">
              <label>Your ideas</label>
              <ReactQuill
                ref={ideaInputRef}
                theme="snow"
                className="quick-idea-popup__editor"
                value={form.idea}
                onChange={(value) => {
                  setError("");
                  setForm((prev) => ({ ...prev, idea: value }));
                }}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write the ideas, thoughts, or next steps..."
                readOnly={saving}
              />
            </div>

          <div className="quick-idea-popup__field">
            <label>Attachment</label>
            <div className="quick-idea-popup__attachment-controls">
              <button
                type="button"
                className="quick-idea-popup__add-link"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving || attachments.length >= MAX_ATTACHMENTS}
              >
                <i className="bi bi-paperclip"></i>
                Upload
              </button>
              <span className="quick-idea-popup__helper">(up to 3 files)</span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.ppt,.pptx"
              style={{ display: "none" }}
              onChange={handleAttachmentSelect}
              disabled={saving}
            />

            {attachments.length > 0 ? (
              <div className="quick-idea-popup__attachment-list">
                {attachments.map((attachment) => (
                  <div className="quick-idea-popup__attachment-row" key={attachment.id}>
                    <div className="quick-idea-popup__attachment-meta">
                      <i className="bi bi-paperclip"></i>
                      <div>
                        <div className="quick-idea-popup__attachment-name">{attachment.name}</div>
                        <div className="quick-idea-popup__attachment-size">
                          {attachment.sizeLabel || ""}
                        </div>
                      </div>
                    </div>
                    <div className="quick-idea-popup__attachment-actions">
                      <button
                        type="button"
                        className="quick-idea-popup__icon-btn"
                        onClick={() => openAttachmentPreview(attachment.previewUrl)}
                        aria-label={`Open ${attachment.name} in a new tab`}
                        title="Open in new tab"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>
                      <button
                        type="button"
                        className="quick-idea-popup__icon-btn"
                        onClick={() => removeAttachment(attachment.id)}
                        aria-label={`Remove ${attachment.name}`}
                        title="Remove attachment"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="quick-idea-popup__field">
            <label htmlFor="quick-idea-tags">Tags</label>
            <div className="quick-idea-popup__tag-box">
              {form.tags.map((tag, index) => (
                <span className="quick-idea-popup__tag" key={`tag-${index}`}>
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(index)}
                    aria-label={`Remove ${tag} tag`}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </span>
              ))}

              <input
                id="quick-idea-tags"
                type="text"
                value={tagInput}
                onChange={(event) => {
                  setError("");
                  setTagInput(event.target.value);
                }}
                onKeyDown={handleTagInputKeyDown}
                onBlur={handleTagInputBlur}
                placeholder={
                  form.tags.length >= MAX_TAGS ? "Tag limit reached" : "Type a tag and press Enter"
                }
                disabled={saving || form.tags.length >= MAX_TAGS}
              />

              {(tagInput || form.tags.length > 0) ? (
                <button
                  type="button"
                  className="quick-idea-popup__tag-clear"
                  onClick={clearTags}
                  aria-label="Clear tags"
                  title="Clear tags"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              ) : null}
            </div>
            <div className="quick-idea-popup__helper">
              Press Enter, comma, or Tab to add a tag.
            </div>
            {existingTags.filter(t => !form.tags.includes(t)).filter(t => !tagInput || t.toLowerCase().includes(tagInput.toLowerCase())).length > 0 && (
              <div className="quick-idea-popup__tag-suggestions">
                <span className="quick-idea-popup__helper">Select from existing tags</span>
                <div className="quick-idea-popup__tag-suggestion-list">
                  {existingTags
                    .filter(t => !form.tags.includes(t))
                    .filter(t => !tagInput || t.toLowerCase().includes(tagInput.toLowerCase()))
                    .slice(0, 10)
                    .map(tag => (
                      <button
                        key={tag}
                        type="button"
                        className="quick-idea-popup__tag-suggestion"
                        onClick={() => addTag(tag)}
                        disabled={saving || form.tags.length >= MAX_TAGS}
                      >
                        + {tag}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>

          <div className="quick-idea-popup__footer">
            <button
              type="button"
              className="quick-idea-popup__ghost-btn"
              onClick={handleClose}
              disabled={saving}
            >
              Close
            </button>
            <button
              type="submit"
              className="quick-idea-popup__save-btn"
              disabled={saving || !form.idea.trim()}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check2-circle"></i>
                  {activeIdea ? "Save Changes" : "Save Idea"}
                </>
              )}
            </button>
          </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
}
